import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const test: ContentBlock<"test"> = {
  id: makeId("test-carrier-screening"),
  kind: "test",
  tags: [
    "testing",
    "her",
    "his",
    "both-partners",
    "genetic-screening",
    "pre-cycle",
    "genomic",
  ],
  title: "Expanded Carrier Screening",
  subtitle:
    "A focused carrier-status report that can be generated from whole genome sequencing",
  icon: "🧬",
  body: {
    testName: "Expanded Carrier Screening",
    category: "genetic",
    whatItMeasures:
      "Screens for 200+ recessive genetic conditions, including cystic fibrosis, SMA, fragile X, sickle cell, and Tay-Sachs.\n\nThis is included inside whole genome sequencing, which can produce the same carrier-status findings plus broader diagnostic, reproductive, and pharmacogenomic insights.",
    howItWorks:
      "Blood draw or saliva sample. Technically, Louise can run this from a whole genome sequence and return the focused expanded-carrier report. Both partners should be tested. If both carry the same condition, genetic counseling can review natural conception, prenatal testing, IVF with PGT-M, or donor options.",
    quickFacts: [
      { kind: "sample", value: "Blood or saliva" },
      { kind: "time", value: "5 min draw or saliva kit" },
      { kind: "pain", value: "Needle pinch if blood" },
      { kind: "prep", value: "Best when both partners test" },
      { kind: "results", value: "2-3 weeks" },
    ],
    resultTypes: [
      {
        label: "No carrier variant found",
        meaning:
          "No reportable carrier variant is found in the genes included in the screen.",
        whatToExpect:
          "This lowers risk for the screened conditions but does not eliminate genetic risk, because no carrier screen covers every possible variant.",
      },
      {
        label: "Carrier",
        meaning:
          "One partner carries a variant for a recessive or X-linked condition.",
        whatToExpect:
          "This is common and usually does not mean that person is sick. The next step is often testing the other partner for the same condition.",
      },
      {
        label: "Both partners carry the same condition",
        meaning:
          "Both reproductive partners carry variants in the same recessive-condition gene.",
        whatToExpect:
          "Genetic counseling should review the chance of an affected pregnancy and options such as IVF with PGT-M, prenatal testing, donor gametes, or natural conception with informed planning.",
      },
      {
        label: "Uncertain or incomplete",
        meaning:
          "A variant may be uncertain, a sample may fail, or one partner may not have been tested yet.",
        whatToExpect:
          "Do not make reproductive decisions from an incomplete result. Ask what follow-up testing or counseling is needed.",
      },
    ],
    turnaroundTime: "2-3 weeks",
    atHome: false,
    clinicRequired: false,
    dataPoints: [],
    section: "genetic-screening",
  },
  sources: [
    {
      title:
        "Carrier Screening in the Age of Genomic Medicine (Committee Opinion No. 690)",
      authors: "American College of Obstetricians and Gynecologists",
      journal: "Obstetrics & Gynecology",
      year: 2017,
      url: "https://pubmed.ncbi.nlm.nih.gov/28225425/",
      evidenceLevel: "expert-opinion",
    },
    {
      title:
        "Carrier Screening for Genetic Conditions (Committee Opinion No. 691)",
      authors: "American College of Obstetricians and Gynecologists",
      journal: "Obstetrics & Gynecology",
      year: 2017,
      url: "https://pubmed.ncbi.nlm.nih.gov/28225426/",
      evidenceLevel: "expert-opinion",
    },
    {
      title:
        "Screening for autosomal recessive and X-linked conditions during pregnancy and preconception: a practice resource of the ACMG",
      authors: "Gregg AR et al.",
      journal: "Genetics in Medicine",
      year: 2021,
      url: "https://pubmed.ncbi.nlm.nih.gov/34285390/",
      evidenceLevel: "guideline",
    },
  ],
  readTimeMinutes: 4,
};

registerBlock(test);
